import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card";
import { useDocs, useTemplateDownload } from "lib/mediaQueries";
import "./TemplatesSection.scss";

const DownloadBadge: FC = () => (
  <span className="overview-template__badge" aria-hidden="true">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 3v10.6l3.3-3.3 1.4 1.4L12 17.4l-4.7-4.7 1.4-1.4L12 13.6V3h0zM5 19h14v2H5z" />
    </svg>
  </span>
);

/**
 * Overview section listing downloadable templates (media items of type "doc").
 * Fails soft: a load error renders inline and never breaks the dashboard.
 * Clicking a card fetches the file's presigned URL and downloads it (the
 * backend forces Content-Disposition: attachment on doc URLs).
 */
const TemplatesSection: FC = () => {
  const { t } = useTranslation();
  const { data, isError, isLoading } = useDocs();
  const download = useTemplateDownload();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="overview-templates__state">{t("common.loading")}</div>
    );
  }
  if (isError) {
    return (
      <div className="overview-templates__state">{t("templates.loadError")}</div>
    );
  }

  const docs = data ?? [];
  if (docs.length === 0) {
    return (
      <div className="overview-templates__state">{t("templates.empty")}</div>
    );
  }

  const onDownload = async (id: string): Promise<void> => {
    setError(null);
    setBusyId(id);
    try {
      await download(id);
    } catch {
      setError(t("templates.downloadError"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {error && <div className="overview-templates__error">{error}</div>}
      <div className="overview-templates">
        {docs.map((d) => (
          <button
            key={d.id}
            type="button"
            className="overview-template"
            onClick={() => onDownload(d.id)}
            disabled={busyId === d.id}
          >
            <Card customClass="overview-template__card">
              <DownloadBadge />
              <div className="overview-template__title">{d.title}</div>
              {d.description && (
                <div className="overview-template__desc">{d.description}</div>
              )}
              <span className="overview-template__go">
                {busyId === d.id
                  ? t("templates.downloading")
                  : t("templates.download")}
              </span>
            </Card>
          </button>
        ))}
      </div>
    </>
  );
};

export default TemplatesSection;
